import { lazy, Suspense, useEffect, useState } from "react";
import type { ComponentType } from "react";
import { QueryClient, QueryClientProvider } from "@tanstack/react-query";
import { Elements } from "@stripe/react-stripe-js";
import { Toaster } from "sonner";
import { Navigate, Route, Routes, useLocation } from "react-router-dom";

import { trackPageView } from "./lib/analytics";
import { consumeImpersonationFragment, subscribeAuth } from "./lib/auth";
import { installMarketingTags } from "./lib/marketingTags";
import { MarketingShell } from "./layouts/MarketingShell";
import { OperatorShell } from "./layouts/OperatorShell";
import { OperatorAuthGate } from "./layouts/OperatorAuthGate";
import { TenantAuthGate } from "./layouts/TenantAuthGate";
import { useTheme } from "./lib/ThemeToggle";
import { getStripePromise } from "./lib/stripe";

// Route-level code splitting (SEO audit P0 — was one 1.6MB bundle).
// Every page component is loaded on demand. Vite's manualChunks in
// vite.config.ts groups them by feature directory so a marketing
// visitor doesn't download the operator app, and vice versa.
//
// Helper: React.lazy's default contract expects a module with a
// `default` export, but every NestPlot page component is a NAMED
// export. `lazyNamed` maps a named export onto the {default} shape
// React.lazy wants — the return type is compatible with a normal
// JSX `<Component />` reference.
function lazyNamed<T extends ComponentType<any>>(
  loader: () => Promise<Record<string, unknown>>,
  name: string,
): React.LazyExoticComponent<T> {
  return lazy(async () => {
    const mod = await loader();
    return { default: mod[name] as T };
  });
}

// -- Auth --
const AcceptInvitationPage = lazyNamed(() => import("./features/auth/AcceptInvitationPage"), "AcceptInvitationPage");
const ResetPasswordPage = lazyNamed(() => import("./features/auth/ResetPasswordPage"), "ResetPasswordPage");
const SignInPage = lazyNamed(() => import("./features/auth/SignInPage"), "SignInPage");
const SsoCompletePage = lazyNamed(() => import("./features/auth/SsoCompletePage"), "SsoCompletePage");
const VerifyPage = lazyNamed(() => import("./features/auth/VerifyPage"), "VerifyPage");
const VerifyPendingPage = lazyNamed(() => import("./features/auth/VerifyPendingPage"), "VerifyPendingPage");

// -- Marketing --
const BlogIndexPage = lazyNamed(() => import("./features/marketing/BlogPage"), "BlogIndexPage");
const BlogPostPage = lazyNamed(() => import("./features/marketing/BlogPostPage"), "BlogPostPage");
const ContactPage = lazyNamed(() => import("./features/marketing/ContactPage"), "ContactPage");
const BookDemoPage = lazyNamed(() => import("./features/marketing/BookDemoPage"), "BookDemoPage");
const UnitSizeIndexPage = lazyNamed(() => import("./features/marketing/UnitSizePage"), "UnitSizeIndexPage");
const UnitSizePage = lazyNamed(() => import("./features/marketing/UnitSizePage"), "UnitSizePage");
const CustomersPage = lazyNamed(() => import("./features/marketing/CustomersPage"), "CustomersPage");
const HomePage = lazyNamed(() => import("./features/marketing/HomePage"), "HomePage");
const NotFoundPage = lazyNamed(() => import("./features/marketing/NotFoundPage"), "NotFoundPage");
const PrivacyPage = lazyNamed(() => import("./features/marketing/LegalPage"), "PrivacyPage");
const TermsPage = lazyNamed(() => import("./features/marketing/LegalPage"), "TermsPage");
const SmsTermsPage = lazyNamed(() => import("./features/marketing/LegalPage"), "SmsTermsPage");
const PilotPage = lazyNamed(() => import("./features/marketing/PilotPage"), "PilotPage");
const PilotApplyPage = lazyNamed(() => import("./features/marketing/PilotApplyPage"), "PilotApplyPage");
const PilotVerticalPage = lazyNamed(() => import("./features/marketing/PilotVerticalPage"), "PilotVerticalPage");
const ForVerticalPage = lazyNamed(() => import("./features/marketing/ForVerticalPage"), "ForVerticalPage");
const PricingPage = lazyNamed(() => import("./features/marketing/PricingPage"), "PricingPage");
const ProductPage = lazyNamed(() => import("./features/marketing/ProductPage"), "ProductPage");
const FeaturesPage = lazyNamed(() => import("./features/marketing/FeaturesPage"), "FeaturesPage");
const SignupPage = lazyNamed(() => import("./features/marketing/SignupPage"), "SignupPage");
const VsIndexPage = lazyNamed(() => import("./features/marketing/VsPage"), "VsIndexPage");
const VsPage = lazyNamed(() => import("./features/marketing/VsPage"), "VsPage");
// Competitor-displacement landing pages (ad/SEO capture targets, unlinked from top nav).
const MigrateStorEdgePage = lazyNamed(() => import("./features/marketing/MigratePage"), "MigrateStorEdgePage");
const MigrateSiteLinkPage = lazyNamed(() => import("./features/marketing/MigratePage"), "MigrateSiteLinkPage");
const KeepYourRentPage = lazyNamed(() => import("./features/marketing/KeepYourRentPage"), "KeepYourRentPage");
const StatePage = lazyNamed(() => import("./features/marketing/StatePage"), "StatePage");
const StatesIndexPage = lazyNamed(() => import("./features/marketing/StatesIndexPage"), "StatesIndexPage");
const ToolPage = lazyNamed(() => import("./features/marketing/ToolPage"), "ToolPage");
const ToolsIndexPage = lazyNamed(() => import("./features/marketing/ToolsIndexPage"), "ToolsIndexPage");
const DocsPage = lazyNamed(() => import("./features/marketing/DocsPage"), "DocsPage");

// -- Operator --
const OperatorPricingPage = lazyNamed(() => import("./features/operator/PricingPage"), "PricingPage");
const BillingPage = lazyNamed(() => import("./features/operator/BillingPage"), "BillingPage");
const EntitiesPage = lazyNamed(() => import("./features/operator/EntitiesPage"), "EntitiesPage");
const GatePage = lazyNamed(() => import("./features/operator/GatePage"), "GatePage");
const ImportPage = lazyNamed(() => import("./features/operator/ImportPage"), "ImportPage");
const UnitsAIFillPage = lazyNamed(() => import("./features/operator/UnitsAIFillPage"), "UnitsAIFillPage");
const LeadsPage = lazyNamed(() => import("./features/operator/LeadsPage"), "LeadsPage");
const LeasesPage = lazyNamed(() => import("./features/operator/LeasesPage"), "LeasesPage");
const LeasingPage = lazyNamed(() => import("./features/operator/LeasingPage"), "LeasingPage");
const ToursPage = lazyNamed(() => import("./features/operator/ToursPage"), "ToursPage");
const MaintenancePage = lazyNamed(() => import("./features/operator/MaintenancePage"), "MaintenancePage");
const MoveInWizardPage = lazyNamed(() => import("./features/operator/MoveInWizardPage"), "MoveInWizardPage");
const ReportsPage = lazyNamed(() => import("./features/operator/ReportsPage"), "ReportsPage");
const ApiKeysSettingsPage = lazyNamed(() => import("./features/operator/ApiKeysSettingsPage"), "ApiKeysSettingsPage");
const WebhooksSettingsPage = lazyNamed(() => import("./features/operator/WebhooksSettingsPage"), "WebhooksSettingsPage");
const OnboardingPage = lazyNamed(() => import("./features/onboarding/OnboardingPage"), "OnboardingPage");
const BillingSettingsPage = lazyNamed(() => import("./features/operator/SettingsPage"), "BillingSettingsPage");
const DangerSettingsPage = lazyNamed(() => import("./features/operator/SettingsPage"), "DangerSettingsPage");
const FacilitySettingsPage = lazyNamed(() => import("./features/operator/SettingsPage"), "FacilitySettingsPage");
const GateSettingsPage = lazyNamed(() => import("./features/operator/SettingsPage"), "GateSettingsPage");
const LeaseSettingsPage = lazyNamed(() => import("./features/operator/SettingsPage"), "LeaseSettingsPage");
const NotificationsSettingsPage = lazyNamed(() => import("./features/operator/SettingsPage"), "NotificationsSettingsPage");
const PayoutsSettingsPage = lazyNamed(() => import("./features/operator/SettingsPage"), "PayoutsSettingsPage");
const RegionSettingsPage = lazyNamed(() => import("./features/operator/RegionSettingsPage"), "RegionSettingsPage");
const SettingsShell = lazyNamed(() => import("./features/operator/SettingsPage"), "SettingsShell");
const TeamSettingsPage = lazyNamed(() => import("./features/operator/SettingsPage"), "TeamSettingsPage");
const TenantProtectionSettingsPage = lazyNamed(() => import("./features/operator/SettingsPage"), "TenantProtectionSettingsPage");
const TenantDetailPage = lazyNamed(() => import("./features/operator/TenantDetailPage"), "TenantDetailPage");
const TenantsPage = lazyNamed(() => import("./features/operator/TenantsPage"), "TenantsPage");
const LienSaleDetailPage = lazyNamed(() => import("./features/operator/LienSaleDetailPage"), "LienSaleDetailPage");
const LienSalesPage = lazyNamed(() => import("./features/operator/LienSalesPage"), "LienSalesPage");
const PortfolioDashboardPage = lazyNamed(() => import("./features/operator/PortfolioDashboardPage"), "PortfolioDashboardPage");
const TodayPage = lazyNamed(() => import("./features/operator/TodayPage"), "TodayPage");
const UnitsPage = lazyNamed(() => import("./features/operator/UnitsPage"), "UnitsPage");

// -- Portal (tenant self-serve) --
const TenantLeasePage = lazyNamed(() => import("./features/portal/TenantLeasePage"), "TenantLeasePage");
const TenantLoginPage = lazyNamed(() => import("./features/portal/TenantLoginPage"), "TenantLoginPage");
const TenantMoveOutPage = lazyNamed(() => import("./features/portal/TenantMoveOutPage"), "TenantMoveOutPage");
const TenantOverviewPage = lazyNamed(() => import("./features/portal/TenantOverviewPage"), "TenantOverviewPage");
const TenantPayPage = lazyNamed(() => import("./features/portal/TenantPayPage"), "TenantPayPage");

// -- Rental (public rental / apply flow) --
const ApplyPage = lazyNamed(() => import("./features/rental/ApplyPage"), "ApplyPage");
const ApplicationStatusPage = lazyNamed(() => import("./features/rental/ApplicationStatusPage"), "ApplicationStatusPage");
const CheckoutPage = lazyNamed(() => import("./features/rental/CheckoutPage"), "CheckoutPage");
const RentalConfirmationPage = lazyNamed(() => import("./features/rental/ConfirmationPage"), "RentalConfirmationPage");
const RentalBrowsePage = lazyNamed(() => import("./features/rental/RentalBrowsePage"), "RentalBrowsePage");
const RentalLandingPage = lazyNamed(() => import("./features/rental/RentalLandingPage"), "RentalLandingPage");
const RentalUnitDetailPage = lazyNamed(() => import("./features/rental/UnitDetailPage"), "RentalUnitDetailPage");

// -- Admin --
const AdminBlogPage = lazyNamed(() => import("./features/admin/AdminPages"), "AdminBlogPage");
const AdminCrmPage = lazyNamed(() => import("./features/admin/AdminPages"), "AdminCrmPage");
const AdminCrmCampaignsPage = lazyNamed(() => import("./features/admin/AdminCrmCampaignsPage"), "AdminCrmCampaignsPage");
const AdminEmailPage = lazyNamed(() => import("./features/admin/AdminPages"), "AdminEmailPage");
const AdminGrowthPage = lazyNamed(() => import("./features/admin/AdminPages"), "AdminGrowthPage");
const AdminInquiriesPage = lazyNamed(() => import("./features/admin/AdminPages"), "AdminInquiriesPage");
const AdminPromoPage = lazyNamed(() => import("./features/admin/AdminPages"), "AdminPromoPage");
const AdminReferralsPage = lazyNamed(() => import("./features/admin/AdminPages"), "AdminReferralsPage");
const AdminSubscribersPage = lazyNamed(() => import("./features/admin/AdminPages"), "AdminSubscribersPage");
const AdminToolsPage = lazyNamed(() => import("./features/admin/AdminPages"), "AdminToolsPage");
const AdminUsersPage = lazyNamed(() => import("./features/admin/AdminPages"), "AdminUsersPage");
const AdminUserDetailPage = lazyNamed(() => import("./features/admin/AdminUserDetailPage"), "AdminUserDetailPage");
const AdminShell = lazyNamed(() => import("./layouts/AdminShell"), "AdminShell");

// -- Non-critical shells (used less often; lazy so first-load doesn't
//    ship them). MarketingShell + OperatorShell stay static because
//    they carry the primary landing surfaces. --
const RentalShell = lazyNamed(() => import("./layouts/RentalShell"), "RentalShell");
const TenantShell = lazyNamed(() => import("./layouts/TenantShell"), "TenantShell");

// Suspense fallback — deliberately tiny (SEO benefit is lost if the
// fallback is visible for long, but the alternative of blocking the
// whole render is worse). Marketing pages are prerendered so the
// fallback is only shown after a hydration + SPA nav to another
// route; the actual prerendered HTML is already painted.
function ChunkFallback() {
  return (
    <div className="flex items-center justify-center min-h-[40vh]">
      <div className="text-sub text-sm font-mono">Loading…</div>
    </div>
  );
}

type Surface = "marketing" | "rental" | "portal" | "app";

/** Push a GTM `page_view` event on every SPA route change. analytics
 * is no-op on non-marketing surfaces (see installMarketingTags), so
 * the hook only meaningfully fires on nestplot.com / www.nestplot.com.
 * Document title is read AFTER a microtask delay so per-page useSeo()
 * has had a chance to update the title before we ship it to GA4. */
function usePageViewTracking(): void {
  const location = useLocation();
  useEffect(() => {
    const handle = window.setTimeout(() => {
      trackPageView(
        location.pathname + location.search,
        document.title,
      );
    }, 0);
    return () => window.clearTimeout(handle);
  }, [location.pathname, location.search]);
}

function detectSurface(host: string): Surface {
  if (host.startsWith("app.")) return "app";
  if (host.startsWith("portal.")) return "portal";
  const parts = host.split(".");
  if (parts.length >= 3 && parts[0] !== "www" && parts[0] !== "storops" && parts[0] !== "api") {
    return "rental";
  }
  return "marketing";
}

// One QueryClient per browser. Stale time of 30s avoids over-fetching while
// keeping operator screens reactive within a session.
const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      staleTime: 30_000,
      retry: 1,
      refetchOnWindowFocus: false,
    },
  },
});

// Wipe the React Query cache on every auth-token change. Sign-in,
// sign-out, impersonate, and stop-impersonate all fire
// `subscribeAuth` notifiers (see lib/auth.ts). Without this clear,
// caching identity-scoped responses like `/auth/me` survived a
// sign-in-as-different-user round trip because SignInPage navigates
// via SPA `navigate(next)` rather than a full reload — the next
// account would see the previous account's cached role until the
// query went stale, manifesting as 'Admin' menu link showing for a
// non-admin OR vice-versa. Subscribed at module load so it's wired
// before the first React render.
if (typeof window !== "undefined") {
  subscribeAuth(() => queryClient.clear());
  // Impersonation tabs land with #impersonate=<jwt> — move that token
  // into sessionStorage and strip it from the URL before the first
  // render so the admin's real token (in localStorage) is left alone.
  consumeImpersonationFragment();
  // Inject GTM + CookieYes on the marketing surface only. The loader
  // is a no-op on app / portal / rental hostnames so a misconfigured
  // tag in the GTM container can't accidentally fire conversions for
  // an operator's tenant-portal page view.
  installMarketingTags(detectSurface(window.location.hostname));
}

export default function App() {
  useTheme();
  usePageViewTracking();
  const [stripePromise] = useState(() => getStripePromise());

  const host = typeof window !== "undefined" ? window.location.hostname : "";
  const surface = detectSurface(host);

  // Per-route title/meta is set by `useSeo()` inside each page component;
  // App-level title overrides used to clobber that because child useEffects
  // run before parent useEffects in React. The prerendered HTML shell ships
  // the right title for the initial route, then useSeo() keeps it accurate
  // through SPA navigations.

  // Elements wraps the entire tree so any payment surface (operator + tenant
  // + public rental) can drop a <CardElement /> wherever needed. When the
  // publishable key isn't set, we render without Elements — the screens fall
  // back to a "configure Stripe to enable card payments" message.
  const content =
    surface === "app" ? <OperatorRoutes /> :
    surface === "portal" ? <PortalRoutes /> :
    surface === "rental" ? <RentalRoutes /> :
    <FullRoutes />;

  return (
    <QueryClientProvider client={queryClient}>
      {/* Always mount <Elements>, even when no publishable key is set.
          The CheckoutPage (and any future Stripe-using surface) calls
          useStripe() unconditionally at render time and the hook throws
          "Could not find Elements context" without a provider in the
          tree — even when it would return null. Passing null as the
          stripe prop lets <Elements> install the context with a no-op
          Stripe instance, so useStripe() returns null safely and the
          surface falls through to its "Stripe not configured" message
          instead of crashing the whole React tree to a blank page. */}
      <Elements stripe={stripePromise}>
        <Suspense fallback={<ChunkFallback />}>{content}</Suspense>
      </Elements>
      <Toaster position="top-right" richColors closeButton />
    </QueryClientProvider>
  );
}

function OperatorRoutes() {
  return (
    <Routes>
      <Route path="/signin" element={<SignInPage />} />
      <Route path="/sso/complete" element={<SsoCompletePage />} />
      <Route path="/invitations/:token" element={<AcceptInvitationPage />} />
      <Route path="/signup" element={<SignupPage />} />
      <Route path="/verify" element={<VerifyPage />} />
      <Route path="/verify-pending" element={<VerifyPendingPage />} />
      <Route path="/auth/forgot" element={<ResetPasswordPage mode="request" />} />
      <Route path="/auth/reset" element={<ResetPasswordPage mode="confirm" />} />
      <Route element={<OperatorAuthGate />}>
        <Route path="/app/onboarding" element={<OnboardingPage />} />
        <Route path="/app" element={<OperatorShell />}>
          <Route index element={<Navigate to="today" replace />} />
          <Route path="today" element={<TodayPage />} />
          <Route path="portfolio" element={<PortfolioDashboardPage />} />
          <Route path="lien-sales" element={<LienSalesPage />} />
          <Route path="lien-sales/:id" element={<LienSaleDetailPage />} />
          <Route path="units" element={<UnitsPage />} />
          <Route path="units/ai-fill" element={<UnitsAIFillPage />} />
          <Route path="tenants" element={<TenantsPage />} />
          <Route path="tenants/import" element={<ImportPage />} />
          <Route path="tenants/:slug" element={<TenantDetailPage />} />
          <Route path="leads" element={<LeadsPage />} />
          <Route path="move-in/new" element={<MoveInWizardPage />} />
          <Route path="billing" element={<BillingPage />} />
          <Route path="gate" element={<GatePage />} />
          <Route path="reports" element={<ReportsPage />} />
          <Route path="leases" element={<LeasesPage />} />
          <Route path="maintenance" element={<MaintenancePage />} />
          <Route path="leasing" element={<LeasingPage />} />
          <Route path="tours" element={<ToursPage />} />
          <Route path="pricing" element={<OperatorPricingPage />} />
          <Route path="settings" element={<SettingsShell />}>
            <Route index element={<Navigate to="facility" replace />} />
            <Route path="gate" element={<GateSettingsPage />} />
            <Route path="facility" element={<FacilitySettingsPage />} />
            <Route path="region" element={<RegionSettingsPage />} />
            <Route path="protection" element={<TenantProtectionSettingsPage />} />
            <Route path="payouts" element={<PayoutsSettingsPage />} />
            <Route path="lease" element={<LeaseSettingsPage />} />
            <Route path="team" element={<TeamSettingsPage />} />
            <Route path="entities" element={<EntitiesPage />} />
            <Route path="api" element={<ApiKeysSettingsPage />} />
            <Route path="webhooks" element={<WebhooksSettingsPage />} />
            <Route path="notifications" element={<NotificationsSettingsPage />} />
            <Route path="danger" element={<DangerSettingsPage />} />
            <Route path="billing" element={<BillingSettingsPage />} />
            {/* Retired tabs redirect to a sensible replacement so any
                bookmarked URL or stale email link still lands somewhere. */}
            <Route path="integrations" element={<Navigate to="../entities" replace />} />
            <Route path="pricing" element={<Navigate to="/app/pricing" replace />} />
          </Route>
          <Route path="admin" element={<AdminShell />}>
            <Route index element={<Navigate to="users" replace />} />
            <Route path="users" element={<AdminUsersPage />} />
            <Route path="users/:userId" element={<AdminUserDetailPage />} />
            <Route path="crm" element={<AdminCrmPage />} />
            <Route path="crm/campaigns" element={<AdminCrmCampaignsPage />} />
            <Route path="inquiries" element={<AdminInquiriesPage />} />
            <Route path="blog" element={<AdminBlogPage />} />
            <Route path="subscribers" element={<AdminSubscribersPage />} />
            <Route path="tools" element={<AdminToolsPage />} />
            <Route path="email" element={<AdminEmailPage />} />
            <Route path="promo" element={<AdminPromoPage />} />
            <Route path="referrals" element={<AdminReferralsPage />} />
            <Route path="growth" element={<AdminGrowthPage />} />
          </Route>
        </Route>
      </Route>
      <Route path="/" element={<Navigate to="/app/today" replace />} />
      <Route path="*" element={<NotFoundPage />} />
    </Routes>
  );
}

function PortalRoutes() {
  return (
    <Routes>
      <Route path="/" element={<TenantLoginPage />} />
      <Route path="/:facility/auth/verify" element={<TenantLoginPage />} />
      <Route element={<TenantAuthGate />}>
        <Route path="/portal/:facility" element={<TenantShell />}>
          <Route index element={<TenantOverviewPage />} />
          <Route path="pay" element={<TenantPayPage />} />
          <Route path="lease" element={<TenantLeasePage />} />
          <Route path="move-out" element={<TenantMoveOutPage />} />
          <Route path="gate" element={<TenantOverviewPage />} />
        </Route>
      </Route>
      <Route path="/:facility" element={<TenantLoginPage />} />
      <Route path="*" element={<NotFoundPage />} />
    </Routes>
  );
}

function RentalRoutes() {
  return (
    <Routes>
      <Route element={<RentalShell />}>
        {/* `/` now serves the operator's marketing landing page
            (hero, amenities, about, contact). The unit-picker
            previously at `/` moved to `/units` with a deep-link from
            the hero CTA. */}
        <Route index element={<RentalLandingPage />} />
        <Route path="units" element={<RentalBrowsePage />} />
        <Route path="units/:id" element={<RentalUnitDetailPage />} />
        <Route path="apply" element={<ApplyPage />} />
        <Route path="application/:token" element={<ApplicationStatusPage />} />
        <Route path="reserve/:id" element={<Navigate to="info" replace />} />
        <Route path="reserve/:id/:step" element={<CheckoutPage />} />
        <Route path="reserve/:id/done" element={<RentalConfirmationPage />} />
        <Route path="*" element={<NotFoundPage />} />
      </Route>
    </Routes>
  );
}

function FullRoutes() {
  return (
    <Routes>
      <Route element={<MarketingShell />}>
        <Route index element={<HomePage />} />
        <Route path="pricing" element={<PricingPage />} />
        <Route path="book-demo" element={<BookDemoPage />} />
        {/* Storage unit sizes — the entity axis that actually gets searched.
            See features/marketing/UnitSizePage.tsx. */}
        <Route path="storage-unit-sizes" element={<UnitSizeIndexPage />} />
        <Route path="storage-unit-sizes/:size" element={<UnitSizePage />} />
        <Route path="pilot" element={<PilotPage />} />
        <Route path="pilot/apply" element={<PilotApplyPage />} />
        {/* Per-vertical pilot landing pages — ad-copy ↔ landing-page
            Quality Score boost for the Google Ads test campaign.
            Slugs mirror /for-X (storage, not self_storage). */}
        <Route
          path="pilot/multifamily"
          element={<PilotVerticalPage vertical="multifamily" />}
        />
        <Route
          path="pilot/storage"
          element={<PilotVerticalPage vertical="self_storage" />}
        />
        <Route
          path="pilot/parking"
          element={<PilotVerticalPage vertical="parking" />}
        />
        <Route
          path="pilot/marina"
          element={<PilotVerticalPage vertical="marina" />}
        />
        <Route path="for-storage" element={<ForVerticalPage vertical="self_storage" />} />
        <Route path="for-multifamily" element={<ForVerticalPage vertical="multifamily" />} />
        <Route path="for-parking" element={<ForVerticalPage vertical="parking" />} />
        <Route path="for-marina" element={<ForVerticalPage vertical="marina" />} />
        <Route path="product" element={<ProductPage />} />
        <Route path="features" element={<FeaturesPage />} />
        <Route path="customers" element={<CustomersPage />} />
        <Route path="contact" element={<ContactPage />} />
        <Route path="vs" element={<VsIndexPage />} />
        <Route path="vs/vertical/:vertical" element={<VsIndexPage />} />
        <Route path="vs/:slug" element={<VsPage />} />
        <Route path="vs/:slug/:stateSlug" element={<VsPage />} />
        <Route path="migrate-from-storedge" element={<MigrateStorEdgePage />} />
        <Route path="migrate-from-sitelink" element={<MigrateSiteLinkPage />} />
        <Route path="keep-100-of-your-rent" element={<KeepYourRentPage />} />
        <Route path="states" element={<StatesIndexPage />} />
        <Route path="states/:slug" element={<StatePage />} />
        <Route path="docs" element={<DocsPage />} />
        <Route path="docs/:slug" element={<DocsPage />} />
        <Route path="tools" element={<ToolsIndexPage />} />
        <Route path="tools/vertical/:vertical" element={<ToolsIndexPage />} />
        <Route path="tools/:slug" element={<ToolPage />} />
        <Route path="tools/:slug/:stateSlug" element={<ToolPage />} />
        <Route path="blog" element={<BlogIndexPage />} />
        <Route path="blog/vertical/:vertical" element={<BlogIndexPage />} />
        <Route path="blog/:slug" element={<BlogPostPage />} />
        {/* Vertical landing pages (V-SEO matrix 2026-06-24) — currently
            reuse ProductPage which is vertical-aware in copy. A dedicated
            VerticalLandingPage that pulls in the right competitors + tools
            for each vertical is a follow-on. */}
        <Route path="verticals" element={<ProductPage />} />
        <Route path="verticals/:vertical" element={<ProductPage />} />
        <Route path="privacy" element={<PrivacyPage />} />
        <Route path="terms" element={<TermsPage />} />
        <Route path="sms-terms" element={<SmsTermsPage />} />
      </Route>

      <Route path="/signup" element={<SignupPage />} />
      <Route path="/signin" element={<SignInPage />} />
      <Route path="/sso/complete" element={<SsoCompletePage />} />
      <Route path="/invitations/:token" element={<AcceptInvitationPage />} />
      <Route path="/verify" element={<VerifyPage />} />
      <Route path="/verify-pending" element={<VerifyPendingPage />} />
      <Route path="/auth/forgot" element={<ResetPasswordPage mode="request" />} />
      <Route path="/auth/reset" element={<ResetPasswordPage mode="confirm" />} />

      <Route element={<OperatorAuthGate />}>
        <Route path="/app/onboarding" element={<OnboardingPage />} />
        <Route path="/app" element={<OperatorShell />}>
          <Route index element={<Navigate to="today" replace />} />
          <Route path="today" element={<TodayPage />} />
          <Route path="portfolio" element={<PortfolioDashboardPage />} />
          <Route path="lien-sales" element={<LienSalesPage />} />
          <Route path="lien-sales/:id" element={<LienSaleDetailPage />} />
          <Route path="units" element={<UnitsPage />} />
          <Route path="units/ai-fill" element={<UnitsAIFillPage />} />
          <Route path="tenants" element={<TenantsPage />} />
          <Route path="tenants/import" element={<ImportPage />} />
          <Route path="tenants/:slug" element={<TenantDetailPage />} />
          <Route path="leads" element={<LeadsPage />} />
          <Route path="move-in/new" element={<MoveInWizardPage />} />
          <Route path="billing" element={<BillingPage />} />
          <Route path="gate" element={<GatePage />} />
          <Route path="reports" element={<ReportsPage />} />
          <Route path="leases" element={<LeasesPage />} />
          <Route path="maintenance" element={<MaintenancePage />} />
          <Route path="leasing" element={<LeasingPage />} />
          <Route path="tours" element={<ToursPage />} />
          <Route path="pricing" element={<OperatorPricingPage />} />
          <Route path="settings" element={<SettingsShell />}>
            <Route index element={<Navigate to="facility" replace />} />
            <Route path="gate" element={<GateSettingsPage />} />
            <Route path="facility" element={<FacilitySettingsPage />} />
            <Route path="region" element={<RegionSettingsPage />} />
            <Route path="protection" element={<TenantProtectionSettingsPage />} />
            <Route path="payouts" element={<PayoutsSettingsPage />} />
            <Route path="lease" element={<LeaseSettingsPage />} />
            <Route path="team" element={<TeamSettingsPage />} />
            <Route path="entities" element={<EntitiesPage />} />
            <Route path="api" element={<ApiKeysSettingsPage />} />
            <Route path="webhooks" element={<WebhooksSettingsPage />} />
            <Route path="notifications" element={<NotificationsSettingsPage />} />
            <Route path="danger" element={<DangerSettingsPage />} />
            <Route path="billing" element={<BillingSettingsPage />} />
            {/* Retired tabs redirect to a sensible replacement so any
                bookmarked URL or stale email link still lands somewhere. */}
            <Route path="integrations" element={<Navigate to="../entities" replace />} />
            <Route path="pricing" element={<Navigate to="/app/pricing" replace />} />
          </Route>
          <Route path="admin" element={<AdminShell />}>
            <Route index element={<Navigate to="users" replace />} />
            <Route path="users" element={<AdminUsersPage />} />
            <Route path="users/:userId" element={<AdminUserDetailPage />} />
            <Route path="crm" element={<AdminCrmPage />} />
            <Route path="crm/campaigns" element={<AdminCrmCampaignsPage />} />
            <Route path="inquiries" element={<AdminInquiriesPage />} />
            <Route path="blog" element={<AdminBlogPage />} />
            <Route path="subscribers" element={<AdminSubscribersPage />} />
            <Route path="tools" element={<AdminToolsPage />} />
            <Route path="email" element={<AdminEmailPage />} />
            <Route path="promo" element={<AdminPromoPage />} />
            <Route path="referrals" element={<AdminReferralsPage />} />
            <Route path="growth" element={<AdminGrowthPage />} />
          </Route>
        </Route>
      </Route>

      <Route path="/portal/login" element={<TenantLoginPage />} />
      <Route path="/portal/:facility/auth/verify" element={<TenantLoginPage />} />
      <Route element={<TenantAuthGate />}>
        <Route path="/portal/:facility" element={<TenantShell />}>
          <Route index element={<TenantOverviewPage />} />
          <Route path="pay" element={<TenantPayPage />} />
          <Route path="lease" element={<TenantLeasePage />} />
          <Route path="move-out" element={<TenantMoveOutPage />} />
          <Route path="gate" element={<TenantOverviewPage />} />
        </Route>
      </Route>

      <Route path="/rent" element={<RentalShell />}>
        <Route index element={<RentalBrowsePage />} />
        <Route path="units/:id" element={<RentalUnitDetailPage />} />
        <Route path="reserve/:id" element={<Navigate to="info" replace />} />
        <Route path="reserve/:id/:step" element={<CheckoutPage />} />
        <Route path="reserve/:id/done" element={<RentalConfirmationPage />} />
      </Route>

      <Route path="*" element={<NotFoundPage />} />
    </Routes>
  );
}
